import React, { useState, useEffect, useMemo } from 'react';
import { registrationAPI, productAPI, formatErrorMessage } from '../services/api';
import Header from './Header';
import { Country } from '../shared-types';
import { formatDate } from '../utils/dateUtils';
import { useExcelTableFilter } from './useExcelTableFilter';
import { ExcelHeaderCell, ExcelActiveFiltersBar } from './ExcelHeaderCell';

interface RegistrationsProps {
  user: any;
  onLogout: () => void;
}

const Registrations: React.FC<RegistrationsProps> = ({ user, onLogout }) => {
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [filteredSkus, setFilteredSkus] = useState<any[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: null, // Added for existing registration ID
    country: '',
    sku: '',
    registration_number: '',
    registration_status: 'Active',
    registration_issue_date: '',
    registration_expiry_date: '',
    remarks: '',
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  useEffect(() => {
    fetchData();
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await registrationAPI.getCountries();
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const regRes = await registrationAPI.getRegistrations();
      setRegistrations(regRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  // Country change → immediately load SKUs for that country and reset SKU
  const handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryName = e.target.value;
    setFormData(prev => ({ ...prev, id: null, country: countryName, sku: '' }));
    setFilteredSkus([]);
    setFormError('');
    if (countryName) {
      try {
        const selectedCountryObj = countries.find(c => c.name === countryName);
        // Pass countryName directly as the backend now expects the name
        const response = await productAPI.getProductsByCountry(countryName);
        setFilteredSkus(response.data || []);
      } catch (error) {
        console.error(`Error fetching products for ${countryName}:`, error);
        setFilteredSkus([]);
      }
    }
  };

  const handleEdit = async (reg: any) => {
    const countryName = reg.country?.name || '';
    setFormData({
      id: reg.id,
      country: countryName,
      sku: reg.sku || '',
      registration_number: reg.registration_number || '',
      registration_status: reg.registration_status || 'Active',
      registration_issue_date: reg.registration_issue_date ? new Date(reg.registration_issue_date).toISOString().split('T')[0] : '',
      registration_expiry_date: reg.registration_expiry_date ? new Date(reg.registration_expiry_date).toISOString().split('T')[0] : '',
      remarks: reg.remarks || '',
    });
    setFormError('');
    setShowModal(true);

    if (countryName) {
      try {
        const response = await productAPI.getProductsByCountry(countryName);
        setFilteredSkus(response.data || []);
      } catch (error) {
        console.error(`Error fetching products for ${countryName}:`, error);
        setFilteredSkus([]);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      country: '',
      sku: '',
      registration_number: '',
      registration_status: 'Active',
      registration_issue_date: '',
      registration_expiry_date: '',
      remarks: '',
    });
    setFilteredSkus([]);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('handleSubmit invoked'); // Added log
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    console.log('Submitting form data:', formData); // Log form data before API call
    console.log('Current submitting state:', submitting);
    try {
      let response;
      const registrationData = {
        country: formData.country,
        sku: formData.sku,
        registration_number: formData.registration_number,
        registration_status: formData.registration_status,
        registration_issue_date: formData.registration_issue_date || null,
        registration_expiry_date: formData.registration_expiry_date || null,
        remarks: formData.remarks,
      };

      if (formData.id) {
        // Update existing registration
        response = await registrationAPI.updateRegistration(formData.id, registrationData);
        console.log('Registration updated successfully:', response.data);
      } else {
        // Create new registration
        response = await registrationAPI.createRegistration(registrationData);
        console.log('Registration created successfully:', response.data);
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      console.error('Error creating registration:', error); // Log error response
      setFormError(formatErrorMessage(error, 'Error creating registration'));
    } finally {
      setSubmitting(false);
    }
  };


  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active':
        return 'status-accepted';
      case 'Expired':
        return 'status-risk';
      default:
        return 'status-hold';
    }
  };

  const isExpiringSoon = (expiryDate: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 90 && diffDays > 0;
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const columnAccessors = useMemo(() => ({
    country: (r: any) => r.country?.name || '-',
    sku: (r: any) => r.sku || '-',
    product: (r: any) => r.product?.product_name || '-',
    registration_number: (r: any) => r.registration_number || '-',
    status: (r: any) => r.registration_status || '-',
    issue_date: (r: any) => formatDate(r.registration_issue_date),
    expiry_date: (r: any) => formatDate(r.registration_expiry_date),
    certificate: (r: any) => (r.certificate_path ? '✓ Yes' : '✗ No'),
  }), []);

  const {
    filteredAndSortedData: excelFilteredRegistrations,
    filterState,
    sortState,
    uniqueValuesMap,
    setColumnFilter,
    handleSort,
    clearColumnFilter,
    clearAllFilters,
    activeFilterCount,
  } = useExcelTableFilter(registrations, columnAccessors);

  useEffect(() => {
    setPage(1);
  }, [filterState]);

  const totalPages = Math.max(1, Math.ceil(excelFilteredRegistrations.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedRegistrations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return excelFilteredRegistrations.slice(start, start + pageSize);
  }, [excelFilteredRegistrations, currentPage, pageSize]);

  if (loading) {
    return <div className="loading">Loading registrations...</div>;
  }

  return (
    <div className="main-container">
      <Header user={user} onLogout={onLogout} />

      <div className="panel">
        <div className="panel-header">
          <h2>Product Registrations</h2>
          <button className="submit-button" onClick={() => setShowModal(true)}>
            + Add Registration
          </button>
        </div>

        <ExcelActiveFiltersBar
          activeCount={activeFilterCount}
          isSorted={!!sortState.direction}
          onClearAll={clearAllFilters}
        />

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <ExcelHeaderCell
                    columnKey="country"
                    label="Country"
                    uniqueValues={uniqueValuesMap.country}
                    selectedValues={filterState.country}
                    sortDirection={sortState.columnKey === 'country' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('country', sel)}
                    onSortChange={(dir) => handleSort('country', dir)}
                    onClearFilter={() => clearColumnFilter('country')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="sku"
                    label="SKU"
                    uniqueValues={uniqueValuesMap.sku}
                    selectedValues={filterState.sku}
                    sortDirection={sortState.columnKey === 'sku' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('sku', sel)}
                    onSortChange={(dir) => handleSort('sku', dir)}
                    onClearFilter={() => clearColumnFilter('sku')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="product"
                    label="Product"
                    uniqueValues={uniqueValuesMap.product}
                    selectedValues={filterState.product}
                    sortDirection={sortState.columnKey === 'product' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('product', sel)}
                    onSortChange={(dir) => handleSort('product', dir)}
                    onClearFilter={() => clearColumnFilter('product')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="registration_number"
                    label="Registration Number"
                    uniqueValues={uniqueValuesMap.registration_number}
                    selectedValues={filterState.registration_number}
                    sortDirection={sortState.columnKey === 'registration_number' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('registration_number', sel)}
                    onSortChange={(dir) => handleSort('registration_number', dir)}
                    onClearFilter={() => clearColumnFilter('registration_number')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="status"
                    label="Status"
                    uniqueValues={uniqueValuesMap.status}
                    selectedValues={filterState.status}
                    sortDirection={sortState.columnKey === 'status' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('status', sel)}
                    onSortChange={(dir) => handleSort('status', dir)}
                    onClearFilter={() => clearColumnFilter('status')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="issue_date"
                    label="Issue Date"
                    uniqueValues={uniqueValuesMap.issue_date}
                    selectedValues={filterState.issue_date}
                    sortDirection={sortState.columnKey === 'issue_date' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('issue_date', sel)}
                    onSortChange={(dir) => handleSort('issue_date', dir)}
                    onClearFilter={() => clearColumnFilter('issue_date')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="expiry_date"
                    label="Expiry Date"
                    uniqueValues={uniqueValuesMap.expiry_date}
                    selectedValues={filterState.expiry_date}
                    sortDirection={sortState.columnKey === 'expiry_date' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('expiry_date', sel)}
                    onSortChange={(dir) => handleSort('expiry_date', dir)}
                    onClearFilter={() => clearColumnFilter('expiry_date')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="certificate"
                    label="Certificate"
                    uniqueValues={uniqueValuesMap.certificate}
                    selectedValues={filterState.certificate}
                    sortDirection={sortState.columnKey === 'certificate' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('certificate', sel)}
                    onSortChange={(dir) => handleSort('certificate', dir)}
                    onClearFilter={() => clearColumnFilter('certificate')}
                  />
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRegistrations.map((reg) => (
                <tr
                  key={reg.id}
                  style={{
                    backgroundColor: isExpired(reg.registration_expiry_date)
                      ? '#ffebee'
                      : isExpiringSoon(reg.registration_expiry_date)
                      ? '#fff3e0'
                      : 'inherit',
                  }}
                >
                  <td>{reg.country?.name || "-"}</td>
                  <td>{reg.sku}</td>
                  <td>{reg.product?.product_name}</td>
                  <td>{reg.registration_number}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(reg.registration_status)}`}>
                      {reg.registration_status}
                    </span>
                  </td>
                  <td>
                    {formatDate(reg.registration_issue_date)}
                  </td>
                  <td>
                    {formatDate(reg.registration_expiry_date)}
                    {isExpiringSoon(reg.registration_expiry_date) && (
                      <span style={{ color: '#ff9800', marginLeft: '5px' }}>⚠️ Expiring Soon</span>
                    )}
                    {isExpired(reg.registration_expiry_date) && (
                      <span style={{ color: '#f44336', marginLeft: '5px' }}>❌ Expired</span>
                    )}
                  </td>
                  <td>{reg.certificate_path ? '✓' : '✗'}</td>
                  <td>
                    <button
                      className="nav-button"
                      style={{ padding: '4px 10px', fontSize: '0.85em' }}
                      onClick={() => handleEdit(reg)}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-table-cards">
          {paginatedRegistrations.map((reg) => (
            <div
              key={reg.id}
              className="mobile-card"
              style={{
                backgroundColor: isExpired(reg.registration_expiry_date)
                  ? '#ffebee'
                  : isExpiringSoon(reg.registration_expiry_date)
                  ? '#fff3e0'
                  : '#f8f9fa',
              }}
            >
              <div className="mobile-card-row">
                <span className="mobile-card-label">Country</span>
                <span className="mobile-card-value">{reg.country?.name || "-"}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">SKU</span>
                <span className="mobile-card-value">{reg.sku}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Product</span>
                <span className="mobile-card-value">{reg.product?.product_name}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Registration Number</span>
                <span className="mobile-card-value">{reg.registration_number}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Status</span>
                <span className="mobile-card-value">
                  <span className={`status-badge ${getStatusClass(reg.registration_status)}`}>
                    {reg.registration_status}
                  </span>
                </span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Issue Date</span>
                <span className="mobile-card-value">
                  {formatDate(reg.registration_issue_date)}
                </span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Expiry Date</span>
                <span className="mobile-card-value">
                  {formatDate(reg.registration_expiry_date)}
                  {isExpiringSoon(reg.registration_expiry_date) && (
                    <span style={{ color: '#ff9800', marginLeft: '5px', fontSize: '12px' }}>⚠️ Expiring Soon</span>
                  )}
                  {isExpired(reg.registration_expiry_date) && (
                    <span style={{ color: '#f44336', marginLeft: '5px', fontSize: '12px' }}>❌ Expired</span>
                  )}
                </span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Certificate</span>
                <span className="mobile-card-value">{reg.certificate_path ? '✓ Available' : '✗ Not Available'}</span>
              </div>
              <div className="mobile-card-row" style={{ marginTop: '10px' }}>
                <span className="mobile-card-label">Action</span>
                <span className="mobile-card-value">
                  <button
                    className="nav-button"
                    style={{ padding: '4px 12px', fontSize: '0.85em' }}
                    onClick={() => handleEdit(reg)}
                  >
                    ✏️ Edit
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>

        {excelFilteredRegistrations.length === 0 && (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No registrations found
          </p>
        )}

        {/* Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <button 
            className="nav-button" 
            disabled={currentPage === 1 || loading} 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            style={{ opacity: (currentPage === 1 || loading) ? 0.5 : 1, cursor: (currentPage === 1 || loading) ? 'not-allowed' : 'pointer' }}
          >
            ← Previous
          </button>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569' }}>
            Page {currentPage} of {totalPages} ({excelFilteredRegistrations.length} {excelFilteredRegistrations.length === 1 ? 'registration' : 'registrations'})
          </span>
          <button 
            className="nav-button" 
            disabled={currentPage >= totalPages || loading} 
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            style={{ opacity: (currentPage >= totalPages || loading) ? 0.5 : 1, cursor: (currentPage >= totalPages || loading) ? 'not-allowed' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      </div>


      {/* Add Registration Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <>
              <h2>{formData.id ? 'Edit Registration' : 'Add New Registration'}</h2>
              <form onSubmit={handleSubmit}>

              {/* ── Inline Error Banner ── */}
              {formError && (
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '7px',
                  padding: '10px 14px',
                  marginBottom: '14px',
                  color: '#dc2626',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  ⚠️ {formError}
                </div>
              )}

              {/* ── Step 1: Country Dropdown ── */}
              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.country}
                  onChange={handleCountryChange}
                  required
                  disabled={!!formData.id}
                  style={formData.id ? { background: '#f1f5f9', cursor: 'not-allowed', opacity: 0.8 } : {}}
                >
                  <option value="">— Select Country —</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.name}>{country.name}</option>
                  ))}
                </select>
              </div>

              {/* ── Step 2: SKU Dropdown (filtered by country) ── */}
              <div className="form-group">
                <label>SKU *</label>
                <select
                  value={formData.sku}
                  onChange={async (e) => {
                    const sku = e.target.value;
                    setFormData(prev => ({ ...prev, sku }));
                    setFormError('');

                    if (formData.country && sku) {
                      try {
                        const selectedCountryObj = countries.find(c => c.name === formData.country);
                        const countryId = selectedCountryObj ? selectedCountryObj.id : formData.country;
                        const response = await registrationAPI.getRegistrationByCountryAndSku(countryId, sku);
                        if (response.data && response.data.id) {
                          const registration = response.data;
                          setFormData(prev => ({
                            ...prev,
                            id: registration.id, // Store the ID of the existing registration
                            registration_number: registration.registration_number || '',
                            registration_status: registration.registration_status || 'Active',
                            registration_issue_date: registration.registration_issue_date ? new Date(registration.registration_issue_date).toISOString().split('T')[0] : '',
                            registration_expiry_date: registration.registration_expiry_date ? new Date(registration.registration_expiry_date).toISOString().split('T')[0] : '',
                            remarks: registration.remarks || '',
                          }));
                          setFormError('This product is already registered in this country. You can update the existing registration by modifying the fields.');
                        } else {
                          // No registration found, clear registration-specific fields
                          setFormData(prev => ({
                            ...prev,
                            id: null, // Explicitly reset ID
                            registration_number: '',
                            registration_status: 'Active',
                            registration_issue_date: '',
                            registration_expiry_date: '',
                            remarks: '',
                          }));
                        }
                      } catch (error) {
                        console.error('Error fetching registration by country and SKU:', error);
                        setFormData(prev => ({
                          ...prev,
                          registration_number: '',
                          registration_status: 'Active',
                          registration_issue_date: '',
                          registration_expiry_date: '',
                          remarks: '',
                        }));
                      }
                    }
                  }}
                  required
                  disabled={!formData.country || !!formData.id}
                  style={formData.id ? { background: '#f1f5f9', cursor: 'not-allowed', opacity: 0.8 } : {}}
                >
                  <option value="">— Select SKU —</option>
                  {filteredSkus.map((product) => (
                    <option key={product.id || product.sku_code} value={product.sku_code}>
                      {product.sku_code} — {product.product_name} {product.customer ? `(Customer: ${product.customer})` : ''}
                    </option>
                  ))}
                </select>
                {formData.country && filteredSkus.length === 0 && (
                  <small style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                    No products found for this country
                  </small>
                )}
              </div>

              {/* ── Registration Number ── */}
              <div className="form-group">
                <label>Registration Number *</label>
                <input
                  type="text"
                  value={formData.registration_number}
                  onChange={(e) => { setFormData({ ...formData, registration_number: e.target.value }); setFormError(''); }}
                  required
                  placeholder="e.g. REG-2024-001"

                />
              </div>

              <div className="form-group">
                <label>Registration Status</label>
                <select
                  value={formData.registration_status}
                  onChange={(e) => setFormData({ ...formData, registration_status: e.target.value })}

                >
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div className="form-group">
                <label>Issue Date</label>
                <input
                  type="date"
                  value={formData.registration_issue_date}
                  onChange={(e) => setFormData({ ...formData, registration_issue_date: e.target.value })}

                />
              </div>

              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={formData.registration_expiry_date}
                  onChange={(e) => setFormData({ ...formData, registration_expiry_date: e.target.value })}

                />
              </div>

              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  rows={3}

                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="submit-button" disabled={submitting}>
                  {submitting ? 'Saving…' : (formData.id ? 'Update' : 'Submit')}
                </button>
                <button
                  type="button"
                  className="nav-button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  Cancel
                </button>
              </div>
            </form>
            </>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registrations;

